<script lang="ts">
    import { getImageSizeUrl } from "@/utils/getImageSizeUrl";
    import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
    import { REVIEWS_SORTING_TYPE } from "@apps/reviews/constants";
    import debounce from "debounce";
    import { lastValueFrom, map } from "rxjs";
    import { createEventDispatcher, onMount, tick } from "svelte";

    import BaseButton from "@components/Buttons/BaseButton.svelte";
    import Image from "@components/Image/Image.svelte";
    import EmblaSlider from "@components/Slider/EmblaSlider.svelte";
    import type { EmblaSliderApi } from "@components/Slider/types";

    import ReviewsImagesSliderQuery from "../../queries/ReviewsImagesSliderQuery";
    import ReviewsImagesSliderService from "../../services/ReviewsImagesSliderService";
    import ReviewsImagesSliderArrows from "./ReviewsImagesSliderArrows.svelte";

    export let reviewsImagesSliderService: ReviewsImagesSliderService;
    export let reviewsImagesSliderQuery: ReviewsImagesSliderQuery;
    export let minImagesForSliderVisibility: number;
    export let productId: number | null;

    const dispatch = createEventDispatcher();
    let emblaApi: EmblaSliderApi | null = null;
    let sliderContainerEl: HTMLDivElement | null = null;

    let effectivePerPage = 5;
    let isPrefetchingNextPage = false;
    let slidesCount = 0;

    function recalcPerPage(): void {
        tick().then(() => {
            const containerWidth = sliderContainerEl?.clientWidth || 0;
            const CARD_WIDTH = 134;

            if (containerWidth <= 0) {
                effectivePerPage = 1;
                return;
            }

            effectivePerPage = Math.max(
                1,
                Math.floor(containerWidth / CARD_WIDTH)
            );
        });
    }

    const debouncedRecalcPerPage = debounce(recalcPerPage, 50);

    onMount(() => {
        debouncedRecalcPerPage();

        let resizeObserver: ResizeObserver | null = null;

        if (sliderContainerEl) {
            resizeObserver = new ResizeObserver(debouncedRecalcPerPage);
            resizeObserver.observe(sliderContainerEl);
        } else {
            window.addEventListener("resize", debouncedRecalcPerPage);
        }

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", debouncedRecalcPerPage);
        };
    });

    const reviewsImagesList$ = reviewsImagesSliderQuery.all$;
    const reviewsImagesPaginationMeta$ =
        reviewsImagesSliderQuery.paginationMeta$;
    const reviewsImagesStatus$ = reviewsImagesSliderQuery.status$;

    const loadMoreReviewsImagesSlider = () => {
        if (productId === null) {
            throw new Error("Product ID is not defined");
        }

        return lastValueFrom(
            reviewsImagesSliderService.loadMoreReviewsImagesSlider({
                productId,
                sortingOptions: [REVIEWS_SORTING_TYPE.MOST_RECENT],
                onlyWithPhotos: true,
                offset:
                    $reviewsImagesPaginationMeta$.perPage +
                    $reviewsImagesPaginationMeta$.currentOffset,
                perPage: 10,
            })
        );
    };

    const reviewsImages$ = reviewsImagesList$.pipe(
        map((images) => {
            return images.flatMap((review) => {
                return review.images.map((image, i) => {
                    return {
                        ...image,
                        index: i,
                        reviewUid: review.uid,
                        isReviewIncentivized: review.isIncentivized,
                        reviewTitle: review.title,
                    };
                });
            });
        })
    );

    const handleNext = (hasNextLocal: boolean) => {
        if (!hasNextLocal && hasNextPage($reviewsImagesPaginationMeta$)) {
            loadMoreReviewsImagesSlider();
        }
        emblaApi?.scrollNext();
    };

    const handlePrev = () => {
        emblaApi?.scrollPrev();
    };

    const handleEmblaSelect = async () => {
        if (!emblaApi) return;
        const lastIndex = Math.max(0, slidesCount - 1);
        const currentIndex = emblaApi.selectedScrollSnap();
        const canLoadMore = hasNextPage($reviewsImagesPaginationMeta$);
        const atLastSlide = currentIndex === lastIndex;

        if (atLastSlide && canLoadMore && !isPrefetchingNextPage) {
            isPrefetchingNextPage = true;
            try {
                await loadMoreReviewsImagesSlider();
            } finally {
                isPrefetchingNextPage = false;
            }
        }
    };

    $: slidesCount = Math.max(
        1,
        Math.ceil((($reviewsImages$?.length || 0) as number) / effectivePerPage)
    );

    const handleClick = (reviewUid: string, activeImageIndex: number) => {
        dispatch("openImageReview", {
            reviewUid,
            activeImageIndex,
        });
    };
</script>

{#if $reviewsImages$.length >= minImagesForSliderVisibility}
    <div class="gw-rv-reviews-images-slider" bind:this={sliderContainerEl}>
        <EmblaSlider
            setApi={(api) => (emblaApi = api)}
            perPage={effectivePerPage}
            loop={false}
            controls={true}
            gap={8}
            on:select={handleEmblaSelect}
        >
            <svelte:fragment slot="slides">
                {#each $reviewsImages$ as image, imageIdx (`${image.reviewUid}-${image.id}`)}
                    <div class="gw-rv-reviews-images-slider__item">
                        <BaseButton
                            class="gw-rv-reviews-images-slider__item-image-wrapper"
                            on:click={() =>
                                handleClick(image.reviewUid, image.index)}
                            ariaLabel={"Open review " + image.reviewTitle}
                        >
                            <Image
                                src={getImageSizeUrl(image.sizes, "md") || ""}
                                alt={`product review image ${imageIdx + 1}`}
                                objectFit="cover"
                            />
                        </BaseButton>
                    </div>
                {/each}
            </svelte:fragment>
            <svelte:fragment slot="arrows" let:hasPrev let:hasNext>
                <ReviewsImagesSliderArrows
                    hasNext={hasNext ||
                        hasNextPage($reviewsImagesPaginationMeta$)}
                    {hasPrev}
                    status={$reviewsImagesStatus$}
                    on:nextSlide={() => handleNext(hasNext)}
                    on:prevSlide={handlePrev}
                />
            </svelte:fragment>
        </EmblaSlider>
    </div>
{/if}

<style lang="postcss">
    .gw-rv-reviews-images-slider {
        margin-bottom: 2.5em;
        max-width: 100%;

        &__item {
            position: relative;
            width: 100%;
            display: flex;
            justify-content: center;
            overflow: hidden;

            &:hover :global(img) {
                transform: scale(1.1);
            }

            :global(&-image-wrapper) {
                width: 134px;
                height: 134px;
                transition: transform 0.2s;
                border-radius: 4px;
                overflow: hidden;
            }
        }
    }
</style>
